import { i18n } from "@dehub/theme";
import { Box, LoadingOverlay } from "@mantine/core";
import { useForm } from "@mantine/form";
import { yupResolver } from "mantine-form-yup-resolver";
import { createContext, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router";
import * as yup from "yup";
import { NetworkApi } from "../../apis";
import { ListPageHeader } from "../../components/list";
import { InvoiceFormStepper } from "../../components/sales-invoice/invoice-form-stepper";
import { type InvoiceProductFormHandle } from "../../components/sales-invoice/invoice-product-form";
import { InvoiceSectionList } from "../../components/sales-invoice/invoice-section-list";
import { InvoiceSummary } from "../../components/sales-invoice/invoice-summary";
import { useInvoiceFormData } from "../../hooks/useInvoiceFormData";
import { useInvoiceScrollSpy } from "../../hooks/useInvoiceScrollSpy";
import { useInvoiceSubmit } from "../../hooks/useInvoiceSubmit";
import { computeStepCompletions } from "../../utils/invoice-steps";
import { message } from "../../utils/Message";
import listClasses from "./List.module.css";
import classes from "./NewInvoice.module.css";
import { Page } from "../../components/layout/Page";

const schema = yup.object({
  senderFinUserId: yup.string().optional(),
  senderBranchId: yup.string().required(() => i18n.t("common:mustFill")).nullable(),
  senderAccId: yup.string().required(() => i18n.t("common:mustFill")).nullable(),
  receiverBusinessId: yup.string().required(() => i18n.t("common:mustFill")).nullable(),
  paymentDate: yup.date().required(() => i18n.t("common:mustFill")).nullable(),
  receiverBranchId: yup.string().required(() => i18n.t("common:mustFill")).nullable(),
  description: yup.string().nullable().optional(),
  shippingAmount: yup
    .number()
    .min(0, () => i18n.t("invoice:validation.amountMin"))
    .max(1000000000, () => i18n.t("invoice:validation.amountMax"))
    .typeError(() => i18n.t("common:mustFill"))
    .nullable()
    .optional(),
  discountType: yup.string().required(" ").nullable().optional(),
  discountValue: yup.number().min(0, () => i18n.t("common:mustFill")).typeError(() => i18n.t("common:mustFill")).nullable().optional(),
  additionalLines: yup
    .array()
    .of(
      yup.object().shape({
        additionalLines: yup.boolean().optional(),
        image: yup.string().optional(),
        discountValue: yup.number().typeError(() => i18n.t("common:mustFill")).optional(),
        discountType: yup.string().optional(),
        name: yup.string().required(() => i18n.t("common:mustFill")),
        price: yup.number().moreThan(0, () => i18n.t("common:mustFill")).typeError(() => i18n.t("common:mustFill")).required(() => i18n.t("common:mustFill")),
        quantity: yup.number().min(1, () => i18n.t("common:mustFill")).typeError(() => i18n.t("common:mustFill")).required(() => i18n.t("common:mustFill")),
        unit: yup.string().required(() => i18n.t("common:mustFill")),
      }),
    )
    .optional(),
  lines: yup
    .array()
    .of(
      yup.object().shape({
        quantity: yup.number().min(1, () => i18n.t("common:mustFill")).typeError(() => i18n.t("common:mustFill")).required(() => i18n.t("common:mustFill")).nullable(),
        discountValue: yup.number().min(0, () => i18n.t("common:mustFill")).typeError(() => i18n.t("common:mustFill")).nullable().optional(),
        discountType: yup.string().nullable().optional(),
        warehouseId: yup.string().required(() => i18n.t("common:mustFill")).nullable(),
      }),
    )
    .required(() => i18n.t("common:mustFill"))
    .nullable()
    .optional(),
});

type IFormData = {
  senderFinUserId: string | undefined | any;
  senderStaffId: string | undefined;
  senderBranchId: string | undefined;
  senderAccId: string | undefined;
  receiverBusinessId: string | undefined;
  paymentDate: Date | string | undefined;
  receiverBranchId: string | null;
  description: string | undefined;
  discountType: string | undefined;
  discountValue: number;
  shippingAmount: number | undefined;
  send: boolean;
  attachments: { name: string; description: string; url: string }[];
  lines: any[];
  orderSalesCode: string | null;
  purchaseSalesCode: string | null;
};

const initData = {
  senderFinUserId: undefined,
  senderStaffId: undefined,
  senderBranchId: undefined,
  senderAccId: undefined,
  receiverBusinessId: undefined,
  paymentDate: undefined,
  receiverBranchId: undefined,
  description: undefined,
  discountType: undefined,
  discountValue: undefined,
  shippingAmount: undefined,
  lines: [],
  send: false,
  attachments: [],
  newItems: [],
  additionalLines: [],
};

const BusinessContext = createContext<any>(null);

export const useBusiness = () => {
  const context = useContext(BusinessContext);
  if (!context) {
    throw new Error("useBusiness must be used within a BusinessContext.Provider");
  }
  return context;
};

/** Returns null outside the new-invoice BusinessContext (e.g. amend). */
export const useOptionalBusiness = () => useContext(BusinessContext);

export const SalesInvoiceForms = () => {
  const { t } = useTranslation(["invoice", "common"]);
  const params = useParams();
  const productFormRef = useRef<InvoiceProductFormHandle>(null);

  const { user, isNew, INV_LIST, invoice, businessList, isLoading } = useInvoiceFormData({ id: params.id });

  const [business, setBusiness] = useState<any>(null);
  const getBusiness = async (id: string) => {
    try {
      const res = id ? await NetworkApi.get(id) : null;
      setBusiness(res);
    } catch (error: any) {
      message.error(error?.message || t("invoice:sales.newFailed"));
    }
  };

  const initialValues = useMemo<IFormData & { newItems: any[]; additionalLines: any[] }>(
    () =>
      invoice
        ? {
            ...initData,
            ...invoice,
            lines: (invoice?.lines || []).map((line: any) => ({
              ...line,
              warehouseId: line.warehouseId ?? "",
            })),
            senderBranchId: invoice?.senderBranchId || businessList?.find((item: any) => item.isDefault)?.id,
          }
        : (initData as any),
    [invoice, businessList],
  );

  const form = useForm<IFormData & { newItems: any[]; additionalLines: any[] }>({
    mode: "controlled",
    initialValues,
    validate: yupResolver(schema),
  });

  useEffect(() => {
    if (!invoice || isNew) return;
    form.setValues({
      ...initData,
      ...invoice,
      lines: (invoice?.lines || []).map((line: any) => ({
        ...line,
        warehouseId: line.warehouseId ?? "",
      })),
      senderBranchId: invoice?.senderBranchId || businessList?.find((item: any) => item.isDefault)?.id,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [invoice, businessList, isNew]);

  const { submit, loading } = useInvoiceSubmit({ isNew, id: params.id, userId: user?.id });

  const handleSubmitForm = async (send: boolean) => {
    const result = form.validate();
    if (!result.hasErrors) {
      submit(form.values, send);
      return;
    }
    const firstError = Object.values(result.errors).find((e) => typeof e === "string" && e.trim());
    if (typeof firstError === "string") {
      message.error(firstError);
    } else {
      message.error(t("invoice:sales.missingRequired"));
    }
    console.warn("[sales-invoice/new] validation errors:", result.errors);
  };

  const { activeStep, goToStep } = useInvoiceScrollSpy();

  const stepTitles = useMemo(
    () => [
      t("invoice:step.buyer"),
      t("invoice:step.sender"),
      t("invoice:step.items"),
      t("invoice:step.attachments"),
    ],
    [t],
  );

  const completions = useMemo(() => computeStepCompletions(form.values), [form.values]);

  if (isLoading) {
    return <Box pos="relative" mih={320}><LoadingOverlay visible /></Box>;
  }

  return (
    <BusinessContext.Provider value={{ business, isNew, getBusiness }}>
      <Page className={classes.page}>
        <ListPageHeader
          eyebrow={
            isNew ? t("invoice:newInvoiceEyebrow") : t("invoice:editInvoiceEyebrow")
          }
          title={isNew ? t("invoice:newInvoice") : t("invoice:editInvoice")}
          extra={
            INV_LIST.hasAccess ? (
              <InvoiceFormStepper
                completions={completions}
                activeStep={activeStep}
                stepTitles={stepTitles}
                onStepClick={goToStep}
              />
            ) : null
          }
        />

        {INV_LIST.hasAccess ? (
          <>
            <Box className={classes.layout}>
              <Box className={classes.main}>
                <InvoiceSectionList
                  form={form}
                  activeStep={activeStep}
                  completions={completions}
                  productFormRef={productFormRef}
                />
              </Box>
              <Box className={classes.aside}>
                <InvoiceSummary
                  form={form}
                  onSaveDraft={() => handleSubmitForm(false)}
                  onSend={() => handleSubmitForm(true)}
                  loading={loading}
                />
              </Box>
            </Box>
            <Box className={classes.mobileBar}>
              <InvoiceSummary
                compact
                form={form}
                onSaveDraft={() => handleSubmitForm(false)}
                onSend={() => handleSubmitForm(true)}
                loading={loading}
              />
            </Box>
          </>
        ) : (
          INV_LIST.accessDenied()
        )}
      </Page>
    </BusinessContext.Provider>
  );
};
